'use client';

import { useState } from 'react';
import type { VisaCategorySummary } from '@/lib/visa-pages';
import { VISA_PHRASE_KEYS, VISA_COLORS } from '@/lib/visa-constants';

interface DestinationRow {
  code: string;
  name: string;
  flag: string;
  slug: string;
  durationDays: number | null;
  costUsd: number | null;
}

interface VisaDestinationTableProps {
  categories: Array<{
    category: VisaCategorySummary['category'];
    count: number;
    rows: DestinationRow[];
  }>;
  lang: string;
  phraseLabels: Record<string, string>;
}

export function VisaDestinationTable({ categories, lang, phraseLabels }: VisaDestinationTableProps) {
  const [filter, setFilter] = useState('');
  const lowerFilter = filter.toLowerCase();

  return (
    <div className="mt-8">
      <input
        type="search"
        placeholder="Search destinations…"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        className="input input-bordered mb-6 w-full max-w-md"
        aria-label="Filter destinations"
      />

      {categories.map(({ category, count, rows }) => {
        const filtered = lowerFilter
          ? rows.filter(r => r.name.toLowerCase().includes(lowerFilter))
          : rows;

        if (filtered.length === 0) return null;

        const label = phraseLabels[category] ?? category;
        const colorClass = VISA_COLORS[category];

        return (
          <section key={category} className="mb-8">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-text-primary">
              <span className={`badge ${colorClass}`}>{label}</span>
              <span className="text-sm font-normal text-text-secondary">
                ({lowerFilter ? filtered.length : count})
              </span>
            </h3>

            <div className="overflow-x-auto">
              <table className="table-zebra table w-full">
                <thead>
                  <tr>
                    <th className="text-left">Destination</th>
                    <th className="text-right">Duration</th>
                    <th className="text-right">Cost (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => (
                    <tr key={row.code}>
                      <td>
                        <span className="mr-1.5">{row.flag}</span>
                        {row.slug ? (
                          <a href={`/${row.slug}`} className="hover:underline">{row.name}</a>
                        ) : (
                          row.name
                        )}
                      </td>
                      <td className="text-right">
                        {row.durationDays ? `${row.durationDays} days` : '—'}
                      </td>
                      <td className="text-right">
                        {row.costUsd != null ? `$${row.costUsd}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
